import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import complianceService from '../../services/complianceService';
import toast from 'react-hot-toast';

export const fetchDisputes = createAsyncThunk(
  'dispute/fetchDisputes',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await complianceService.getDisputes(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch disputes');
    }
  }
);

export const fetchDispute = createAsyncThunk(
  'dispute/fetchDispute',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await complianceService.getDispute(id);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch dispute');
    }
  }
);

export const createDispute = createAsyncThunk(
  'dispute/createDispute',
  async (disputeData, { rejectWithValue }) => {
    try {
      const { data } = await complianceService.createDispute(disputeData);
      toast.success('Dispute filed successfully');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to file dispute');
    }
  }
);

export const resolveDispute = createAsyncThunk(
  'dispute/resolveDispute',
  async ({ id, resolution }, { rejectWithValue }) => {
    try {
      const { data } = await complianceService.resolveDispute(id, resolution);
      toast.success('Dispute resolved');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to resolve dispute');
    }
  }
);

const initialState = {
  disputes: [],
  currentDispute: null,
  totalCount: 0,
  loading: false,
  detailLoading: false,
  error: null,
};

const disputeSlice = createSlice({
  name: 'dispute',
  initialState,
  reducers: {
    clearCurrentDispute: (state) => { state.currentDispute = null; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDisputes.pending, (state) => { state.loading = true; })
      .addCase(fetchDisputes.fulfilled, (state, action) => {
        state.loading = false;
        state.disputes = (() => {
          const _d = action.payload;
          // The disputes API returns { disputes: [...], total }. Also
          // tolerate results/items/array shapes from other callers.
          return Array.isArray(_d?.disputes) ? _d.disputes
            : Array.isArray(_d?.results) ? _d.results
            : Array.isArray(_d?.items) ? _d.items
            : Array.isArray(_d) ? _d : [];
        })();
        state.totalCount = action.payload?.total ?? action.payload?.count ?? 0;
      })
      .addCase(fetchDisputes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchDispute.pending, (state) => { state.detailLoading = true; })
      .addCase(fetchDispute.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.currentDispute = action.payload;
      })
      .addCase(fetchDispute.rejected, (state) => { state.detailLoading = false; })
      .addCase(createDispute.fulfilled, (state, action) => {
        state.disputes.unshift(action.payload);
      })
      .addCase(resolveDispute.fulfilled, (state, action) => {
        state.currentDispute = action.payload;
        const idx = state.disputes.findIndex((d) => d.id === action.payload.id);
        if (idx !== -1) state.disputes[idx] = action.payload;
      });
  },
});

export const { clearCurrentDispute, clearError } = disputeSlice.actions;
export default disputeSlice.reducer;
